import {
    BoundaryMode,
    CRAWL_CONFIG_DEFAULTS,
    CrawlConfig,
    CrawlSchedule,
    ReviewPolicy
} from "../../common/interfaces/admin/crawl.interface";

/**
 * Form state for the web site crawl settings (web_scraper_provider_design.md
 * BR-34, §10). Lists are edited as one-entry-per-line text; numbers stay
 * strings until submit so a half-typed value never snaps back.
 */
export interface CrawlSettingsValues {
    seedUrls: string;
    boundaryMode: BoundaryMode;
    boundaryPrefixes: string;
    maxDepth: string;
    maxPages: string;
    maxDocuments: string;
    /** The crawl time limit in minutes (stored as maxRunMs). */
    maxRunMinutes: string;
    includeDocuments: boolean;
    includeMedia: boolean;
    useSitemap: boolean;
    followLinks: boolean;
    /** Empty on create = the platform's standard exclusions. */
    excludePatterns: string;
    includePattern: string;
    crawlSchedule: CrawlSchedule;
    reviewPolicy: ReviewPolicy;
    robotsOverride: boolean;
    /** D6: the explicit attestation that accompanies a robots override. */
    robotsAttested: boolean;
    /** Crawl result email recipients, one per line (design §8.6). */
    notifyEmails: string;
}

export const BOUNDARY_LABELS: Record<BoundaryMode, string> = {
    host: "This host only",
    host_and_subdomains: "This host and its subdomains",
    prefixes: "Only these URL prefixes"
};

export const SCHEDULE_LABELS: Record<CrawlSchedule, string> = {
    off: "Off (refresh manually)",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly"
};

export const REVIEW_POLICY_LABELS: Record<ReviewPolicy, string> = {
    always: "Always review before indexing",
    auto: "Index automatically when nothing looks risky"
};

export const emptyCrawlSettings = (): CrawlSettingsValues => ({
    seedUrls: "",
    boundaryMode: "host",
    boundaryPrefixes: "",
    maxDepth: String(CRAWL_CONFIG_DEFAULTS.maxDepth),
    maxPages: String(CRAWL_CONFIG_DEFAULTS.maxPages),
    maxDocuments: String(CRAWL_CONFIG_DEFAULTS.maxDocuments),
    maxRunMinutes: String(CRAWL_CONFIG_DEFAULTS.maxRunMinutes),
    includeDocuments: CRAWL_CONFIG_DEFAULTS.includeDocuments,
    includeMedia: CRAWL_CONFIG_DEFAULTS.includeMedia,
    useSitemap: CRAWL_CONFIG_DEFAULTS.useSitemap,
    followLinks: CRAWL_CONFIG_DEFAULTS.followLinks,
    excludePatterns: "",
    includePattern: "",
    crawlSchedule: "off",
    reviewPolicy: "always",
    robotsOverride: false,
    robotsAttested: false,
    notifyEmails: ""
});

/** Seed the form from a stored provider (edit mode). */
export const crawlSettingsFromProvider = (provider: {
    crawlConfig?: Partial<CrawlConfig> | null;
    crawlSchedule?: CrawlSchedule | null;
    reviewPolicy?: ReviewPolicy | null;
    robotsOverride?: boolean;
}): CrawlSettingsValues => {
    const config = provider.crawlConfig ?? {};
    const base = emptyCrawlSettings();
    return {
        ...base,
        seedUrls: (config.seedUrls ?? []).join("\n"),
        boundaryMode: config.boundary?.mode ?? "host",
        boundaryPrefixes: (config.boundary?.prefixes ?? []).join("\n"),
        maxDepth: String(config.maxDepth ?? base.maxDepth),
        maxPages: String(config.maxPages ?? base.maxPages),
        maxDocuments: String(config.maxDocuments ?? base.maxDocuments),
        maxRunMinutes: config.maxRunMs
            ? String(Math.max(1, Math.round(config.maxRunMs / 60000)))
            : base.maxRunMinutes,
        includeDocuments: config.includeDocuments ?? base.includeDocuments,
        includeMedia: config.includeMedia ?? base.includeMedia,
        useSitemap: config.useSitemap ?? base.useSitemap,
        followLinks: config.followLinks ?? base.followLinks,
        excludePatterns: (config.excludePatterns ?? []).join("\n"),
        includePattern: config.includePattern ?? "",
        crawlSchedule: provider.crawlSchedule ?? "off",
        reviewPolicy: provider.reviewPolicy ?? "always",
        robotsOverride: !!provider.robotsOverride,
        robotsAttested: !!provider.robotsOverride,
        notifyEmails: (config.notifyEmails ?? []).join("\n")
    };
};

const lines = (text: string): string[] =>
    text
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);

const isHttpUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === "https:" || url.protocol === "http:";
    } catch {
        return false;
    }
};

/** Most recipients per source; the server enforces the same number. */
export const MAX_NOTIFY_EMAILS = 20;

/** Recipients typed one per line; commas and semicolons separate too. */
export const notifyEmailList = (text: string): string[] =>
    text
        .split(/[\r\n,;]+/)
        .map(entry => entry.trim())
        .filter(Boolean);

const isEmail = (value: string): boolean =>
    /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]+$/.test(value);

const isRegex = (value: string): boolean => {
    try {
        new RegExp(value);
        return true;
    } catch {
        return false;
    }
};

const positiveInt = (value: string): number | null => {
    const n = Number(value);
    return Number.isInteger(n) && n > 0 ? n : null;
};

/** Host of the first seed URL — named in the robots attestation (D6). */
export const seedHost = (values: Pick<CrawlSettingsValues, "seedUrls">): string => {
    const first = lines(values.seedUrls)[0];
    if (!first) return "";
    try {
        return new URL(first).host;
    } catch {
        return "";
    }
};

/** Client-side checks; the server re-validates and applies tenant ceilings. */
export const validateCrawlSettings = (values: CrawlSettingsValues): string[] => {
    const errors: string[] = [];
    const seeds = lines(values.seedUrls);
    if (!seeds.length) errors.push("Enter at least one start URL.");
    seeds
        .filter(seed => !isHttpUrl(seed))
        .forEach(seed =>
            errors.push(
                `"${seed}" is not a valid web address (it must start with https:// or http://).`
            )
        );
    if (values.boundaryMode === "prefixes") {
        const prefixes = lines(values.boundaryPrefixes);
        if (!prefixes.length) errors.push("Enter at least one URL prefix to stay within.");
        prefixes
            .filter(prefix => !isHttpUrl(prefix))
            .forEach(prefix => errors.push(`"${prefix}" is not a valid URL prefix.`));
    }
    if (positiveInt(values.maxDepth) === null)
        errors.push("Link depth must be a whole number above zero.");
    if (positiveInt(values.maxPages) === null)
        errors.push("Page limit must be a whole number above zero.");
    if (positiveInt(values.maxDocuments) === null) {
        errors.push("Document limit must be a whole number above zero.");
    }
    if (positiveInt(values.maxRunMinutes) === null) {
        errors.push("Crawl time must be a whole number of minutes above zero.");
    }
    lines(values.excludePatterns)
        .filter(pattern => !isRegex(pattern))
        .forEach(pattern =>
            errors.push(`Exclude pattern "${pattern}" is not a valid regular expression.`)
        );
    if (values.includePattern.trim() && !isRegex(values.includePattern.trim())) {
        errors.push("The include pattern is not a valid regular expression.");
    }
    const recipients = notifyEmailList(values.notifyEmails);
    recipients
        .filter(email => !isEmail(email))
        .forEach(email => errors.push(`"${email}" is not a valid email address.`));
    if (recipients.length > MAX_NOTIFY_EMAILS) {
        errors.push(`Enter at most ${MAX_NOTIFY_EMAILS} email addresses for crawl results.`);
    }
    if (values.robotsOverride && !values.robotsAttested) {
        errors.push(
            "Confirm that your organization operates this site before ignoring its robots.txt rules."
        );
    }
    return errors;
};

/**
 * The crawlConfig payload. An empty exclude list on create is omitted so the
 * server fills in its standard exclusions; in edit mode an empty list is sent
 * as-is (the admin cleared it on purpose).
 */
export const toCrawlConfig = (
    values: CrawlSettingsValues,
    mode: "create" | "edit"
): Partial<CrawlConfig> => {
    const excludePatterns = lines(values.excludePatterns);
    const config: Partial<CrawlConfig> = {
        seedUrls: lines(values.seedUrls),
        boundary:
            values.boundaryMode === "prefixes"
                ? { mode: "prefixes", prefixes: lines(values.boundaryPrefixes) }
                : { mode: values.boundaryMode },
        maxDepth: positiveInt(values.maxDepth) ?? CRAWL_CONFIG_DEFAULTS.maxDepth,
        maxPages: positiveInt(values.maxPages) ?? CRAWL_CONFIG_DEFAULTS.maxPages,
        maxDocuments: positiveInt(values.maxDocuments) ?? CRAWL_CONFIG_DEFAULTS.maxDocuments,
        maxRunMs:
            (positiveInt(values.maxRunMinutes) ?? CRAWL_CONFIG_DEFAULTS.maxRunMinutes) * 60000,
        includeDocuments: values.includeDocuments,
        includeMedia: values.includeMedia,
        useSitemap: values.useSitemap,
        followLinks: values.followLinks,
        includePattern: values.includePattern.trim() || null,
        notifyEmails: notifyEmailList(values.notifyEmails)
    };
    if (mode === "edit" || excludePatterns.length) config.excludePatterns = excludePatterns;
    return config;
};
